import { ELearning } from "tf-api-client";
import type { FencyMood } from "../../shared/fency/fency";

/**
 * Moods the dashboard asks Fency for, e.g. a lesson step's `avatarMood`.
 *
 * The five API moods come first. The rest are frontend-ready extras that the
 * backend `AvatarMood` enum has to accept before authors can use them;
 * unknown values fall back to `informative`.
 */
export type CopilotMood =
  | ELearning.AvatarMood
  /** Firm, focused: a rule to follow ("Always verify the sender"). */
  | "assertive"
  /** Questioning: a prompt to think ("Spot the red flag"). */
  | "curious"
  /** An active threat or high-risk mistake. */
  | "alert";

/**
 * How each mood shows on the shared Fency engine (src/shared/fency), which
 * has fewer expressions: a happy squint for good news, the startled alert
 * for a threat, and its lively idle (blinks, glances, gestures) otherwise.
 * A warning stays idle rather than alarmed, so it never scolds the learner.
 * Keyed by the moods' string values (the API sends them lowercase).
 */
const ENGINE_MOOD: Record<string, FencyMood> = {
  informative: "idle",
  positive: "happy",
  reassuring: "idle",
  warning: "idle",
  conclusive: "happy",
  assertive: "idle",
  curious: "idle",
  alert: "alert",
};

export function normalizeMood(mood: string | undefined | null): CopilotMood {
  const key = (mood ?? "").trim().toLowerCase();
  return key in ENGINE_MOOD
    ? (key as CopilotMood)
    : ELearning.AvatarMood.Informative;
}

export function engineMood(mood: string | undefined | null): FencyMood {
  return ENGINE_MOOD[normalizeMood(mood)];
}
